import { Store } from '@ngrx/store';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import {
    AppSku,
    EntityType,
    GeoFileImportStatusCompletedItemContract,
    MapItemType,
    RiderColor,
} from '@polaris-customerweb/sharedcomponents.core';
import { GeoBounds } from '@polaris-customerweb/sharedcomponents.shared';
import {
    Geofence,
    PlannedRide,
    TrackedRide,
    Waypoint,
} from '@polaris-customerweb/sharedcomponents.user-content';
import { compact, flatten } from 'lodash-es';
import {
    BehaviorSubject,
    catchError,
    combineLatest,
    filter,
    map,
    NEVER,
    Observable,
    of,
    switchMap,
    take,
    takeWhile,
    timer,
} from 'rxjs';
import { ForceCollectionRefresh } from 'src/app/components/common/collection/collection.actions';
import {
    ForceGeofenceRefresh,
    SetActiveGeofence,
} from 'src/app/components/geofence/geofence.actions';
import {
    ForcePlannedRideRefresh,
    SetActivePlannedRide,
} from 'src/app/components/planned-ride/planned-ride.actions';
import {
    ForceTrackedRideRefresh,
    SetActiveTrackedRide,
} from 'src/app/components/tracked-ride/tracked-ride.actions';
import {
    ForceWaypointRefresh,
    SetActiveWaypoint,
} from 'src/app/components/waypoint/waypoint.actions';
import { AppState } from 'src/app/models/app-state';
import { Collection } from 'src/app/models/collection';
import {
    GeoFileImport,
    GeoFileImportItem,
    GeoFileImportRideType,
} from 'src/app/models/geo-file-import';
import { GeoFileUploadCollectionCheckbox } from 'src/app/models/geo-file-upload-collection-checkbox';
import { MapDisplay } from 'src/app/models/map-display';
import { GeofenceMapService } from 'src/app/services/geofence-map.service';
import { MapContentMapService } from 'src/app/services/map-content-map.service';
import { MapService } from 'src/app/services/map.service';
import { PlannedRideMapService } from 'src/app/services/planned-ride-map.service';
import { RideTypeService } from 'src/app/services/ride-type.service';
import { TrackedRideMapService } from 'src/app/services/tracked-ride-map.service';
import { WaypointMapService } from 'src/app/services/waypoint-map.service';
import {
    SetIsShowingGeofences,
    SetIsShowingPlannedRides,
    SetIsShowingTrackedRides,
    SetIsShowingWaypoints,
} from '../map/layer-visibility/layer-visibility.actions';
import {
    selectIsShowingGeofences,
    selectIsShowingPlannedRides,
    selectIsShowingTrackedRides,
    selectIsShowingWaypoints,
} from '../map/layer-visibility/layer-visibility.reducer';
import { GeoFileImportService } from './geo-file-import.service';

export class GeoFileImportVm {
    plannedRides$ = new BehaviorSubject<PlannedRide[]>([]);
    trackedRides$ = new BehaviorSubject<TrackedRide[]>([]);
    geofences$ = new BehaviorSubject<Geofence[]>([]);
    waypoints$ = new BehaviorSubject<Waypoint[]>([]);
    all$ = new BehaviorSubject<GeoFileUploadCollectionCheckbox[]>([]);
    isEditing = false;
    selectedCollection$ = this.all$
        .asObservable()
        .pipe(
            map((selections) =>
                selections
                    .filter((selection) => selection.checked)
                    .map((selection) => selection.feature)
            )
        );
    item: PlannedRide | TrackedRide | Geofence | Waypoint | Collection;
    type: EntityType;
    itemNamePlaceholder: string;
    bounds: Observable<GeoBounds>;
    rideType: GeoFileImportRideType;
    file: File;
    isDestroyed = false;
    errorMessage = '';
    errorMessageDetail = '';

    mapDisplay: MapDisplay;
    percentageComplete = 0;
    submitting = false;
    processing = false;
    importCount = 0;

    private plannedRideIcons =
        '/assets/icons/map-icons/shared/planned-ride.svg';
    private trackedRideIcon = '/assets/icons/map-icons/shared/tracked-ride.svg';
    private waypointIcon = '/assets/icons/map-icons/shared/waypoint.svg';
    private geofenceIcon = '/assets/icons/map-icons/shared/geofence.svg';
    private isPlannedRideVisibilityChanged = false;
    private isTrackedRidesVisibilityChanged = false;
    private isWaypointsVisibilityChanged = false;
    private isGeofencesVisibilityChanged = false;
    private sessionId: string | undefined;
    private ids: string[];
    private errorImportingFileMessage = $localize`There was an error importing your file.`;

    constructor(
        private configService: ConfigService,
        private geofenceMapService: GeofenceMapService,
        private geoFileImportService: GeoFileImportService,
        private mapService: MapService,
        private plannedRideMapService: PlannedRideMapService,
        private rideTypeService: RideTypeService,
        private store: Store<AppState>,
        private trackedRideMapService: TrackedRideMapService,
        private waypointMapService: WaypointMapService
    ) {}

    get itemColor(): RiderColor | undefined {
        return this.item instanceof Collection ? undefined : this.item.color;
    }
    set itemColor(color: RiderColor | undefined) {
        if (this.item instanceof Collection || !color) {
            return;
        }
        if (this.item instanceof PlannedRide) {
            this.item.ride.setColor(color);
        } else if (this.item instanceof TrackedRide) {
            this.item.setColor(color);
        } else {
            this.item.color = color;
        }
        this.refreshMap();
    }

    // todo: verify date field to use
    get itemDate(): Date | undefined {
        return this.item instanceof TrackedRide
            ? this.item.creationDate
            : undefined;
    }

    get itemLength(): number | undefined {
        return this.item instanceof TrackedRide
            ? this.item.totalLengthMeters
            : this.item instanceof PlannedRide
              ? this.item.ride.totalLengthInMeters
              : undefined;
    }

    get itemDuration(): number | undefined {
        return this.item instanceof TrackedRide
            ? this.item.totalDurationSeconds
            : undefined;
    }

    get itemSize(): number | undefined {
        return this.item instanceof Geofence ? this.item.area : undefined;
    }

    get itemCount(): number | undefined {
        return this.item instanceof Collection
            ? this.all$.value.length
            : undefined;
    }

    get selectedItemCount(): number | undefined {
        return this.item instanceof Collection
            ? this.all$.value.filter((all) => all.checked).length
            : undefined;
    }

    get isCollection(): boolean {
        return this.item instanceof Collection;
    }

    // only tracked rides and planned rides need ride type selector currently
    get showRideTypeSelector(): boolean {
        return (
            this.configService.isOffRoad &&
            (this.plannedRides$.value.length > 0 ||
                this.trackedRides$.value.length > 0)
        );
    }

    get pageTitle(): string {
        if (this.item instanceof PlannedRide) {
            return $localize`Import Route`;
        } else if (this.item instanceof TrackedRide) {
            return $localize`Import Track`;
        } else if (this.item instanceof Geofence) {
            return $localize`Import Area`;
        } else if (this.item instanceof Waypoint) {
            return $localize`Import Place`;
        } else {
            return $localize`Import Rides and Places`;
        }
    }

    uploadFile(file: File): Observable<void> {
        this.errorMessage = this.errorMessageDetail = '';
        return this.geoFileImportService.uploadGeoFile(file).pipe(
            map((res) => {
                this.sessionId = res.sessionId;
                this.ids = res.features.map((feat) => feat.id);
                this.bounds = of(this.calculateBounds(res.features));
                if (res.features.length === 1) {
                    this.type = res.features[0].type;
                    this.item = res.features[0];

                    switch (this.item.type) {
                        case 'PlannedRide':
                            this.plannedRides$.next([this.item as PlannedRide]);
                            this.itemNamePlaceholder = $localize`Name`;
                            this.store.dispatch(
                                new SetActivePlannedRide(
                                    this.item as PlannedRide
                                )
                            );
                            if (this.configService.isOffRoad) {
                                const rideType = (this.item as PlannedRide)
                                    .rideType;
                                this.rideType =
                                    rideType === 'SNO' ? 'Snow' : 'OffRoad';
                                this.rideTypeService.setRideType(rideType);
                            }
                            break;
                        case 'TrackedRide':
                            this.trackedRides$.next([this.item as TrackedRide]);
                            this.itemNamePlaceholder = $localize`Name`;
                            this.store.dispatch(
                                new SetActiveTrackedRide(
                                    this.item as TrackedRide
                                )
                            );
                            if (this.configService.isOffRoad) {
                                const rideType = (this.item as TrackedRide)
                                    .rideType;
                                this.rideType =
                                    rideType === 'SNO' ? 'Snow' : 'OffRoad';
                                this.rideTypeService.setRideType(rideType);
                            }
                            break;
                        case 'Geofence':
                            this.geofences$.next([this.item as Geofence]);
                            this.itemNamePlaceholder = $localize`Name`;
                            this.store.dispatch(
                                new SetActiveGeofence(this.item as Geofence)
                            );
                            if (this.configService.isOffRoad) {
                                this.rideType = 'OffRoad';
                                this.rideTypeService.setRideType('ORV');
                            }
                            break;
                        case 'Waypoint':
                            this.waypoints$.next([this.item as Waypoint]);
                            this.itemNamePlaceholder = $localize`Name`;
                            this.store.dispatch(
                                new SetActiveWaypoint(this.item as Waypoint)
                            );
                            if (this.configService.isOffRoad) {
                                const types = (this.item as Waypoint).rideTypes;
                                this.rideType =
                                    types.includes('ORV') &&
                                    types.includes('SNO')
                                        ? 'Both'
                                        : types.includes('SNO')
                                          ? 'Snow'
                                          : 'OffRoad';
                                this.rideTypeService.setRideType(
                                    this.rideType === 'Snow' ? 'SNO' : 'ORV'
                                );
                            }
                            break;
                    }
                } else {
                    let hasOrv = false;
                    const plannedRides = res.features.filter(
                        (x) => x instanceof PlannedRide
                    );
                    const trackedRides = res.features.filter(
                        (x) => x instanceof TrackedRide
                    );
                    const geofences = res.features.filter(
                        (x) => x instanceof Geofence
                    );
                    const waypoints = res.features.filter(
                        (x) => x instanceof Waypoint
                    );
                    const plannedRideCheckboxes = plannedRides.map(
                        (feature) => {
                            const ride = new GeoFileUploadCollectionCheckbox(
                                feature
                            );
                            ride.icon = this.plannedRideIcons;
                            ride.itemLength = feature.ride.totalLengthInMeters;
                            hasOrv = hasOrv || feature.rideType === 'ORV';
                            return ride;
                        }
                    );
                    const trackedRideCheckboxes = trackedRides.map(
                        (feature) => {
                            const ride = new GeoFileUploadCollectionCheckbox(
                                feature
                            );
                            ride.icon = this.trackedRideIcon;
                            ride.itemLength = feature.totalLengthMeters;
                            ride.itemDuration = feature.totalDurationSeconds;
                            hasOrv = hasOrv || feature.rideType === 'ORV';
                            return ride;
                        }
                    );
                    const geofenceCheckboxes = geofences.map((feature) => {
                        const geofence = new GeoFileUploadCollectionCheckbox(
                            feature
                        );
                        geofence.icon = this.geofenceIcon;
                        geofence.itemSize = feature.area;
                        return geofence;
                    });
                    const waypointCheckboxes = waypoints.map((feature) => {
                        const point = new GeoFileUploadCollectionCheckbox(
                            feature
                        );
                        point.icon = this.waypointIcon;
                        return point;
                    });

                    this.plannedRides$.next(plannedRides);
                    this.trackedRides$.next(trackedRides);
                    this.geofences$.next(geofences);
                    this.waypoints$.next(waypoints);
                    this.all$.next([
                        ...plannedRideCheckboxes,
                        ...trackedRideCheckboxes,
                        ...geofenceCheckboxes,
                        ...waypointCheckboxes,
                    ]);

                    this.item = new Collection({
                        name:
                            res.collectionName ||
                            this.file.name.replace(/\.[^.]+$/, '') ||
                            $localize`Collection Name`,
                    });

                    // If there is no planned ride and tracked ride, mark the ride type as ORV
                    hasOrv =
                        hasOrv ||
                        (plannedRides.length === 0 &&
                            trackedRides.length === 0);

                    this.rideType = hasOrv ? 'OffRoad' : 'Snow';
                    this.rideTypeService.setRideType(
                        this.rideType === 'Snow' ? 'SNO' : 'ORV'
                    );

                    this.displayCollectionOnMap();
                }
            }),
            catchError((error) => {
                this.setErrorMessage(error);
                return of(undefined);
            })
        );
    }

    initMap(mapDisplay: MapDisplay) {
        this.mapDisplay = mapDisplay;
        if (!(this.item instanceof Collection)) {
            return;
        }
        mapDisplay.mapItemHover$
            .pipe(
                takeWhile(() => !this.isDestroyed),
                filter(
                    (x) =>
                        !this.isEditing &&
                        [
                            'plannedRide',
                            'trackedRide',
                            'geofence',
                            'waypoint',
                            'emptyArea',
                        ].includes(x.itemType)
                )
            )
            .subscribe((item) => {
                if (item.itemType === 'emptyArea') {
                    this.plannedRideMapService.setSelectedFilter(
                        mapDisplay.map
                    );
                    this.trackedRideMapService.setSelectedFilter(
                        mapDisplay.map
                    );
                    this.geofenceMapService.setSelectedFilter(mapDisplay.map);
                    this.waypointMapService.setSelectedFilter(mapDisplay.map);
                    this.mapService.setCursorNormal(mapDisplay.map);
                    this.all$.value.forEach(
                        (listItem) => (listItem.hoveredOnMap = false)
                    );
                } else {
                    this.getMapServiceFromMapItem(item).setSelectedFilter(
                        mapDisplay.map,
                        item.id
                    );
                    this.mapService.setCursorPointer(mapDisplay.map);
                    const listItem = this.all$.value.find(
                        (allItem) => allItem.feature.id === item.id
                    );
                    if (listItem) {
                        listItem.hoveredOnMap = true;
                    }
                }
            });
    }

    highlight(item: PlannedRide | TrackedRide | Geofence | Waypoint) {
        if (!this.isEditing && this.mapDisplay?.map) {
            this.getMapServiceFromItem(item).setSelectedFilter(
                this.mapDisplay.map,
                item.id
            );
        }
    }

    unhighlight() {
        if (!this.isEditing) {
            this.plannedRideMapService.setSelectedFilter(this.mapDisplay.map);
            this.trackedRideMapService.setSelectedFilter(this.mapDisplay.map);
            this.geofenceMapService.setSelectedFilter(this.mapDisplay.map);
            this.waypointMapService.setSelectedFilter(this.mapDisplay.map);
        }
    }

    toggleSelectAllItem() {
        const all = this.all$.getValue();
        const checked = this.itemCount !== this.selectedItemCount;
        this.all$.next(
            all.map((item) => {
                item.checked = checked;
                return item;
            })
        );
    }

    editItem(editItem: GeoFileUploadCollectionCheckbox) {
        this.unhighlight();
        this.highlight(editItem.feature);
        for (const item of this.all$.value) {
            item.editing = item.feature.id === editItem.feature.id;
        }
        this.isEditing = true;
    }

    stopEditItem(item: GeoFileUploadCollectionCheckbox) {
        item.editing = false;
        this.isEditing = false;
    }

    toggleCollectionItemColorPicker(item: GeoFileUploadCollectionCheckbox) {
        for (const listItem of this.all$.value) {
            if (listItem.feature.id === item.feature.id) {
                listItem.editingColor = !listItem.editingColor;
            } else {
                listItem.editingColor = false;
            }
        }
    }

    setCollectionItemColor(
        item: GeoFileUploadCollectionCheckbox,
        color: RiderColor
    ) {
        if (item.feature instanceof PlannedRide) {
            item.feature.ride.setColor(color);
        } else if (item.feature instanceof TrackedRide) {
            item.feature.setColor(color);
        } else if (!(item.feature instanceof Collection)) {
            item.feature.color = color;
        }
        item.editingColor = false;
        this.refreshMap();
    }

    refreshMap() {
        let items: GeoFileUploadCollectionCheckbox[];

        switch (this.item.type) {
            case 'PlannedRide':
                this.plannedRides$.next([this.item as PlannedRide]);
                break;
            case 'TrackedRide':
                this.trackedRides$.next([this.item as TrackedRide]);
                break;
            case 'Geofence':
                this.geofences$.next([this.item as Geofence]);
                break;
            case 'Waypoint':
                this.waypoints$.next([this.item as Waypoint]);
                break;
            default:
                items = this.all$.value;
                this.plannedRides$.next(
                    items
                        .filter((x) => x.feature instanceof PlannedRide)
                        .map((x) => x.feature) as PlannedRide[]
                );
                this.trackedRides$.next(
                    items
                        .filter((x) => x.feature instanceof TrackedRide)
                        .map((x) => x.feature) as TrackedRide[]
                );
                this.geofences$.next(
                    items
                        .filter((x) => x.feature instanceof Geofence)
                        .map((x) => x.feature) as Geofence[]
                );
                this.waypoints$.next(
                    items
                        .filter((x) => x.feature instanceof Waypoint)
                        .map((x) => x.feature) as Waypoint[]
                );
                break;
        }
    }

    importFile() {
        const geoImport = {
            sessionId: this.sessionId,
        } as GeoFileImport;
        const oldRideType: AppSku = this.configService.isOffRoad
            ? this.rideType === 'OffRoad'
                ? 'ORV'
                : 'SNO'
            : this.configService.appSku;
        if (this.item instanceof Collection) {
            geoImport.name = this.item.name;
            geoImport.description = this.item.description;
            geoImport.isCollection = true;
            // put items back in original order to get the index correct for the import
            // do not include unselected items
            geoImport.items = compact(
                this.ids.map((id, index) => {
                    const checkbox = this.all$.value.find(
                        (box) => box.feature.id === id
                    );
                    return checkbox?.checked
                        ? GeoFileImportItem.fromItem(
                              checkbox.feature,
                              this.rideType,
                              oldRideType,
                              index
                          )
                        : undefined;
                })
            );
        } else {
            geoImport.items = [
                GeoFileImportItem.fromItem(
                    this.item,
                    this.rideType,
                    oldRideType
                ),
            ];
        }
        this.submitting = true;
        return this.geoFileImportService.importGeoFile(geoImport).pipe(
            switchMap(() => this.process()),
            catchError((error) => {
                this.setErrorMessage(error);
                return of(null);
            })
        );
    }

    process() {
        this.importCount = this.all$.value.filter((x) => x.checked).length || 1;
        if (this.importCount !== 1) {
            this.processing = true;
            this.submitting = false;
        }
        const additional = Math.ceil(this.importCount * 0.25);
        this.percentageComplete =
            (additional / (this.importCount + additional)) * 100;
        return timer(0, 3000).pipe(
            switchMap(() =>
                this.geoFileImportService.getImportStatus(this.sessionId || '')
            ),
            takeWhile(() => !this.isDestroyed),
            switchMap((res) => {
                this.percentageComplete =
                    Math.ceil(
                        (res.itemsAttemptedCount + additional) /
                            (res.totalItemsCount + additional)
                    ) * 100;
                if (res.itemsAttemptedCount === res.totalItemsCount) {
                    if (res.completed.find((x) => x.type === 'TrackedRide')) {
                        this.store.dispatch(new ForceTrackedRideRefresh(true));
                    }
                    if (res.completed.find((x) => x.type === 'PlannedRide')) {
                        this.store.dispatch(new ForcePlannedRideRefresh(true));
                    }
                    if (res.completed.find((x) => x.type === 'Waypoint')) {
                        this.store.dispatch(new ForceWaypointRefresh(true));
                    }
                    if (res.completed.find((x) => x.type === 'Geofence')) {
                        this.store.dispatch(new ForceGeofenceRefresh(true));
                    }
                    if (
                        res.completed.find(
                            (x) => x.type === 'ContentCollection'
                        )
                    ) {
                        this.store.dispatch(new ForceCollectionRefresh(true));
                    }
                    this.processing = false;
                    return of(res.completed);
                }
                return NEVER;
            })
        );
    }

    getImportedItemUrl(items: GeoFileImportStatusCompletedItemContract[]) {
        // navigate to associated collection or first item
        const item =
            items.find((x) => x.type === 'ContentCollection') || items[0];
        switch (item.type) {
            case 'ContentCollection':
                return ['rides', 'collection', item.id];
            case 'TrackedRide':
                return ['rides', 'tracked-ride', item.id];
            case 'PlannedRide':
                return ['rides', 'planned-ride', item.id];
            case 'Waypoint':
                return ['rides', 'waypoint', item.id];
            default:
                return ['rides', 'geofence', item.id];
        }
    }

    changeRideType() {
        this.rideTypeService.setRideType(
            this.rideType === 'Snow' ? 'SNO' : 'ORV'
        );
    }

    destroy() {
        this.isDestroyed = true;
        this.store.dispatch(new SetActivePlannedRide(undefined));
        this.store.dispatch(new SetActiveTrackedRide(undefined));
        this.store.dispatch(new SetActiveGeofence(undefined));
        this.store.dispatch(new SetActiveWaypoint(undefined));
        this.setCollectionVisibility(false);
    }

    private calculateBounds(
        list: (PlannedRide | TrackedRide | Geofence | Waypoint)[]
    ): GeoBounds {
        return GeoBounds.fromGeoCoordinates(
            flatten(
                list.map((item) => {
                    if (item instanceof PlannedRide) {
                        const bounds = GeoBounds.fromGeoCoordinates(
                            item.ride.geoCoordinates
                        );
                        return [bounds.northEast, bounds.southWest];
                    } else {
                        return [item.bounds.northEast, item.bounds.southWest];
                    }
                })
            )
        ).resizedBy(1.2);
    }

    private getMapServiceFromMapItem(item: {
        itemType: MapItemType;
        id?: string;
    }): MapContentMapService {
        switch (item.itemType) {
            case 'plannedRide':
                return this.plannedRideMapService;
            case 'trackedRide':
                return this.trackedRideMapService;
            case 'geofence':
                return this.geofenceMapService;
            default:
                return this.waypointMapService;
        }
    }

    private getMapServiceFromItem(
        item: PlannedRide | TrackedRide | Geofence | Waypoint
    ): MapContentMapService {
        switch (item.type) {
            case 'PlannedRide':
                return this.plannedRideMapService;
            case 'TrackedRide':
                return this.trackedRideMapService;
            case 'Geofence':
                return this.geofenceMapService;
            default:
                return this.waypointMapService;
        }
    }

    private displayCollectionOnMap(): void {
        combineLatest([
            this.store.select(selectIsShowingPlannedRides),
            this.store.select(selectIsShowingTrackedRides),
            this.store.select(selectIsShowingWaypoints),
            this.store.select(selectIsShowingGeofences),
        ])
            .pipe(take(1))
            .subscribe(
                ([
                    isShowingPlannedRides,
                    isShowingTrackedRides,
                    isShowingWaypoints,
                    isShowingGeofences,
                ]) => {
                    // Only change visibility of item previously hidden
                    this.isPlannedRideVisibilityChanged =
                        !isShowingPlannedRides;
                    this.isTrackedRidesVisibilityChanged =
                        !isShowingTrackedRides;
                    this.isWaypointsVisibilityChanged = !isShowingWaypoints;
                    this.isGeofencesVisibilityChanged = !isShowingGeofences;

                    this.setCollectionVisibility(true);
                }
            );
    }

    private setCollectionVisibility(visible: boolean): void {
        if (this.isPlannedRideVisibilityChanged) {
            this.store.dispatch(new SetIsShowingPlannedRides(visible));
        }

        if (this.isTrackedRidesVisibilityChanged) {
            this.store.dispatch(new SetIsShowingTrackedRides(visible));
        }

        if (this.isWaypointsVisibilityChanged) {
            this.store.dispatch(new SetIsShowingWaypoints(visible));
        }

        if (this.isGeofencesVisibilityChanged) {
            this.store.dispatch(new SetIsShowingGeofences(visible));
        }
    }

    private setErrorMessage(error): void {
        this.errorMessage = this.errorImportingFileMessage;
        if (error?.error?.detail) {
            this.errorMessage = this.errorMessage.replace('.', ':');
            this.errorMessageDetail = error.error.detail;
        }
        this.submitting = false;
    }
}
